/**
 * Centralized Permissions Matrix for CONNECT
 * 
 * This file defines all role-based access control for the application.
 * Both navigation (Layout.jsx) and route protection (App.js) should reference this file.
 * 
 * ROLES (in order of increasing privilege):
 * - standard: Basic user - can make calls, view contacts, manage own profile
 * - phone_admin: Phone administrator - standard + call history, analytics
 * - customer_admin: Company administrator - phone_admin + company settings, user management
 * - developer: System developer - full access to all features including system settings
 */

// Role hierarchy - each role includes all permissions of roles below it
export const ROLE_HIERARCHY = {
  standard: ['standard'],
  phone_admin: ['standard', 'phone_admin'],
  customer_admin: ['standard', 'phone_admin', 'customer_admin'],
  developer: ['standard', 'phone_admin', 'customer_admin', 'developer']
};

// Helper to check if a role has access to a required role level
export const hasRoleAccess = (userRole, requiredRole) => {
  if (!requiredRole || requiredRole === 'all') return true;
  const userRoles = ROLE_HIERARCHY[userRole] || ROLE_HIERARCHY.standard;
  return userRoles.includes(requiredRole);
};

/**
 * PERMISSIONS MATRIX
 * 
 * Each page/route has:
 * - path: The URL path
 * - requiredRole: Minimum role required ('all' means any authenticated user)
 * - showInNav: Whether to show in sidebar navigation
 * - navConfig: Navigation display config (icon, label, testId) - only if showInNav is true
 */
export const PERMISSIONS_MATRIX = {
  // === CORE PAGES (All Users) ===
  dialer: {
    path: '/app/dialer',
    requiredRole: 'all',
    showInNav: true,
    navOrder: 1,
    description: 'Main softphone dialer'
  },
  contacts: {
    path: '/app/contacts',
    requiredRole: 'all',
    showInNav: true,
    navOrder: 2,
    description: 'Contact management'
  },
  myCalls: {
    path: '/app/my-calls',
    requiredRole: 'all',
    showInNav: true,
    navOrder: 3,
    description: 'Personal call recordings and voicemails'
  },
  queueMonitor: {
    path: '/app/queues',
    requiredRole: 'all',
    showInNav: true,
    navOrder: 4,
    description: 'Live queue monitoring'
  },
  profile: {
    path: '/app/profile',
    requiredRole: 'all',
    showInNav: false, // Accessed via user menu, not sidebar
    description: 'User profile and preferences'
  },
  
  // === ADMIN PAGES (Phone Admin+) ===
  callHistory: {
    path: '/app/history',
    requiredRole: 'phone_admin',
    showInNav: true,
    navOrder: 5,
    description: 'Company-wide call history and CDR'
  },
  analytics: {
    path: '/app/analytics',
    requiredRole: 'phone_admin',
    showInNav: true,
    navOrder: 6,
    description: 'Call analytics and statistics'
  },
  
  // === COMPANY ADMIN PAGES (Customer Admin+) ===
  companySettings: {
    path: '/app/company',
    requiredRole: 'customer_admin',
    showInNav: true,
    navOrder: 7,
    description: 'Company settings and user management'
  },
  
  // === DEVELOPER PAGES (Developer Only) ===
  developers: {
    path: '/app/developers',
    requiredRole: 'developer',
    showInNav: false, // Accessed via settings icon
    description: 'System-wide developer settings'
  },
  
  // === INFO PAGES (All Authenticated Users) ===
  about: {
    path: '/app/about',
    requiredRole: 'all',
    showInNav: false, // Footer link
    description: 'About the application'
  },
  help: {
    path: '/app/help',
    requiredRole: 'all',
    showInNav: false, // Footer link
    description: 'Help and FAQ'
  },
  privacyApp: {
    path: '/app/privacy',
    requiredRole: 'all',
    showInNav: false, // Footer link
    description: 'Privacy policy (in-app)'
  },
  termsApp: {
    path: '/app/terms',
    requiredRole: 'all',
    showInNav: false, // Footer link
    description: 'Terms and conditions (in-app)'
  }
};

// Navigation icon mapping (to avoid importing icons in config)
export const NAV_ICONS = {
  dialer: 'Headset',
  contacts: 'Users',
  myCalls: 'PhoneCall',
  queueMonitor: 'UsersRound',
  callHistory: 'History',
  analytics: 'BarChart3',
  companySettings: 'Building2'
};

// Navigation labels
export const NAV_LABELS = {
  dialer: 'My Phone',
  contacts: 'Contacts',
  myCalls: 'My Calls',
  queueMonitor: 'Queue Monitor',
  callHistory: 'Call History',
  analytics: 'Analytics',
  companySettings: 'Company'
};

// Get navigation items for a specific role
export const getNavItemsForRole = (userRole) => {
  return Object.entries(PERMISSIONS_MATRIX)
    .filter(([key, config]) => {
      if (!config.showInNav) return false;
      return hasRoleAccess(userRole, config.requiredRole === 'all' ? 'standard' : config.requiredRole);
    })
    .sort((a, b) => a[1].navOrder - b[1].navOrder)
    .map(([key, config]) => ({
      key,
      path: config.path,
      icon: NAV_ICONS[key],
      label: NAV_LABELS[key],
      testId: `nav-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`
    }));
};

// Check if user can access a specific page
export const canAccessPage = (userRole, pageKey) => {
  const config = PERMISSIONS_MATRIX[pageKey];
  if (!config) return false;
  return hasRoleAccess(userRole, config.requiredRole === 'all' ? 'standard' : config.requiredRole);
};

export default PERMISSIONS_MATRIX;
